import { AssetGroup } from '@play-co/timestep-core';
import {
  ImageScaleView,
  RichTextHorizontalAlign,
  RichTextVerticalAlign,
  RichTextView,
  View,
} from '@play-co/timestep-core/ui';
import { downscale } from 'src/conf/scale';
import PopupBasic from 'src/game/components/popups/PopupBasic';
import i18n from 'src/lib/i18n/i18n';
import sounds from 'src/lib/sounds';
import ButtonScaleViewWithText from 'src/lib/ui/components/ButtonScaleViewWithText';
import buttons from 'src/lib/ui/config/buttons';
import { Feature } from 'src/replicant/analytics';
import { getRescueLevelTheme } from '../rescueLevel.theme';
import LangBitmapFontTextView from 'src/lib/ui/components/LangBitmapFontTextView';
import { DreamBubble } from '../components/DreamBubble';
import { bitmapFontFromFontID } from 'src/lib/bitmapFonts';
import { user } from 'src/StateObserver';
import { rescueLevelGetNextLevel } from 'src/replicant/rescueLevel/rescueLevel.getters';

type CloseHandler = (ok: boolean) => void;

export default class PopupRescueLevelPlay extends PopupBasic {
  private playButton: ButtonScaleViewWithText;
  private skipButton: ButtonScaleViewWithText;
  private dreamBubble: DreamBubble;
  private playNext: ImageScaleView;
  private playNextText: RichTextView;

  constructor(opts: { superview: View; close: CloseHandler }) {
    super({
      superview: opts.superview,
      boxOpts: {
        image: 'assets/minigame/rescueLevel/popup.png',
      },
      skipTitle: true,
      width: downscale(1015),
      height: downscale(1218),
      darkerBg: true,
      offsetY: -100,
      close: () => {
        opts.close(false);
      },
      tracking: {
        feature: Feature.RESCUE_LEVEL._,
        $subFeature: Feature.RESCUE_LEVEL.POPUP_PLAY,
      },
      zIndex: 10000,
    });

    this.buttonClose.updateOpts({
      x: this.box.style.width - 71,
      y: 250,
    });

    // Title
    new LangBitmapFontTextView({
      superview: this.box,
      align: 'center',
      verticalAlign: 'center',
      size: 38,
      font: 'Body',
      width: 440,
      height: 100,
      color: '#ffffff',
      centerOnOrigin: true,
      x: this.box.style.width / 2,
      y: 240,
      localeText: () => i18n('rescueLevel.popupPlay.title'),
    });

    const BUTTON_HEIGHT = 80;

    this.skipButton = new ButtonScaleViewWithText({
      superview: this.box,
      ...buttons.cancel,
      x: this.box.style.width * 0.5 - 130,
      y: this.box.style.height - BUTTON_HEIGHT,
      fontSize: 38,
      labelOffsetY: -2,
      font: 'Body',
      width: 220,
      height: BUTTON_HEIGHT,
      centerOnOrigin: true,
      onClick: async () => {
        sounds.sfx.play('cancel');
        opts.close(false);
      },
      tracking: {
        feature: Feature.RESCUE_LEVEL._,
        $subFeature: Feature.RESCUE_LEVEL.SKIP,
      },
    });

    this.playButton = new ButtonScaleViewWithText({
      superview: this.box,
      ...buttons.secondary,
      x: this.box.style.width * 0.5 + 130,
      y: this.box.style.height - BUTTON_HEIGHT,
      fontSize: 38,
      labelOffsetY: -2,
      font: 'Body',
      width: 220,
      height: BUTTON_HEIGHT,
      centerOnOrigin: true,
      onClick: async () => {
        sounds.sfx.play('ok');
        opts.close(true);
      },
      tracking: {
        feature: Feature.RESCUE_LEVEL._,
        $subFeature: Feature.RESCUE_LEVEL.PLAY,
      },
    });

    this.dreamBubble = new DreamBubble({
      superview: this.box,
      x: 450,
      y: 420,
    });

    this.playNext = new ImageScaleView({
      superview: this.box,
      image: 'assets/minigame/rescueLevel/rounded_frame_dark.png',
      width: downscale(708),
      height: downscale(185),
      centerOnOrigin: true,
      x: this.box.style.width / 2,
      y: this.box.style.height + 200,
    });

    this.playNextText = new RichTextView({
      superview: this.playNext,
      size: 34,
      align: RichTextHorizontalAlign.Center,
      verticalAlign: RichTextVerticalAlign.Center,
      font: bitmapFontFromFontID('Title')!,
      width: this.playNext.style.width - 40,
      height: this.playNext.style.height - 10,
      color: '#ffffff',
      centerOnOrigin: true,
      x: this.playNext.style.width / 2,
      y: this.playNext.style.height / 2 - 5,
    });
  }

  public getAssetsGroup() {
    return new AssetGroup([
      'assets/minigame/rescueLevel/popup.png',
      'assets/minigame/rescueLevel/rounded_frame_dark.png',
      ...getRescueLevelTheme().getAssets(),
    ]);
  }

  public async init(args: { retry?: boolean }) {
    super.init({});
    this.skipButton.localeText = () => i18n('rescueLevel.popupPlay.skip');
    this.playButton.localeText = () =>
      i18n(
        args.retry
          ? 'rescueLevel.popupPlay.retry'
          : 'rescueLevel.popupPlay.play',
      );

    const nextLevel = rescueLevelGetNextLevel(user());

    // If not a retry or no next level, hide the play next info
    if (!args.retry || !nextLevel) {
      this.playNext.hide();
      return;
    }

    this.playNext.show();
    this.playNextText.text = i18n('rescueLevel.popupPlay.next', {
      num: nextLevel,
    });
  }

  public async fadeIn() {
    await super.fadeIn();
    sounds.playSound('gachaItemDrop');
  }
}
